import { useState } from 'react';
import { Search } from 'lucide-react';
import { cn } from '@/shared/utils/cn';
import type { BillableQuickFilter } from '../../../hooks/useEngagementMetricsLogData';

const QUICK_FILTERS: { value: BillableQuickFilter; label: string }[] = [
  { value: 'ALL', label: 'All' },
  { value: 'BILLABLE', label: 'Billable' },
  { value: 'NON_BILLABLE', label: 'Non-billable' },
];

const REPORTING_TYPE_OPTIONS = [
  { value: '', label: 'All Types' },
  { value: 'FIRST_MESSAGE', label: 'First message' },
  { value: 'RESPONDED_MESSAGE', label: 'Responded message' },
  { value: 'FP_SP_FIRST_MESSAGE', label: 'FA–SP First Message' },
  { value: 'FP_SP_FOLLOW_UP', label: 'FA–SP Follow-up' },
  { value: 'CONSULTATION_REQUEST', label: 'Consultation request' },
  { value: 'REFERRAL', label: 'Referral' },
  { value: 'PROFILE_VIEW', label: 'Profile view' },
  { value: 'NETWORK_CONNECTION', label: 'Network connection' },
  { value: 'RESOURCE_SHARED', label: 'Resource Shared' },
  { value: 'RESOURCE_VIEW', label: 'Resource Viewed' },
  { value: 'SP_OUTBOUND_FIRST_MESSAGE', label: 'SP-outbound first message' },
];

const INITIATOR_ROLE_OPTIONS = [
  { value: '', label: 'All roles' },
  { value: 'CARE_SEEKER', label: 'Care Seeker' },
  { value: 'SERVICE_PROVIDER', label: 'Service Provider' },
  { value: 'FINANCIAL_ADVISOR', label: 'Financial Advisor' },
  { value: 'ADMIN', label: 'Admin' },
  { value: 'GUEST', label: 'Guest' },
];

type EngagementLogFiltersProps = {
  search: string;
  onSearchChange: (value: string) => void;
  billableFilter: BillableQuickFilter;
  onBillableFilterChange: (value: BillableQuickFilter) => void;
  reportingType: string;
  onReportingTypeChange: (value: string) => void;
  initiatorRole: string;
  onInitiatorRoleChange: (value: string) => void;
};

/**
 * ponytail: "More filters" reveals plain single-select dropdowns for Initiator Role and
 * Engagement Type rather than the pixel-spec's multi-select checkbox popover — the log
 * endpoint already accepts comma-separated values for both, so upgrading to true multi-select
 * later is a UI-only change with no backend work.
 */
export const EngagementLogFilters = ({
  search,
  onSearchChange,
  billableFilter,
  onBillableFilterChange,
  reportingType,
  onReportingTypeChange,
  initiatorRole,
  onInitiatorRoleChange,
}: EngagementLogFiltersProps) => {
  const [showMoreFilters, setShowMoreFilters] = useState(false);

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[240px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Search name, company, email, or ID..."
            aria-label="Search engagements"
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#15543e]/20 focus:border-[#15543e]"
          />
        </div>

        <div
          className="flex rounded-md border border-gray-300 overflow-hidden"
          role="group"
          aria-label="Billable quick filter"
        >
          {QUICK_FILTERS.map(({ value, label }) => (
            <button
              key={value}
              data-active={billableFilter === value ? 'true' : 'false'}
              onClick={() => onBillableFilterChange(value)}
              className={cn(
                'px-3 py-1.5 text-sm font-medium transition-colors',
                billableFilter === value
                  ? 'bg-[#15543e] text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-50',
              )}
            >
              {label}
            </button>
          ))}
        </div>

        <select
          value={reportingType}
          onChange={(e) => onReportingTypeChange(e.target.value)}
          aria-label="Engagement type"
          className="px-3 py-1.5 text-sm border border-gray-300 rounded-md bg-white text-gray-700"
        >
          {REPORTING_TYPE_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>

        <button
          type="button"
          onClick={() => setShowMoreFilters((prev) => !prev)}
          aria-expanded={showMoreFilters}
          className="px-3 py-1.5 text-sm font-medium text-gray-600 border border-gray-300 rounded-md bg-white hover:bg-gray-50"
        >
          More filters {showMoreFilters ? '▾' : '▸'}
        </button>
      </div>

      {showMoreFilters && (
        <div className="flex items-center gap-3 flex-wrap">
          <label className="flex items-center gap-2 text-sm text-gray-600">
            Initiator Role
            <select
              value={initiatorRole}
              onChange={(e) => onInitiatorRoleChange(e.target.value)}
              aria-label="Initiator role"
              className="px-3 py-1.5 text-sm border border-gray-300 rounded-md bg-white text-gray-700"
            >
              {INITIATOR_ROLE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
        </div>
      )}
    </div>
  );
};
